import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import FAQSection from "@/components/ui/faq-section";
import { AuroraHero } from "@/components/ui/aurora-hero";
import { 
  Target, 
  AlertTriangle, 
  FileText, 
  BarChart3,
  CheckCircle2,
  Settings,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  MonitorPlay,
  Users,
  Clock,
  Award,
  Search,
  Calendar,
  Archive,
  Eye,
  Download,
  BookOpen,
  ListChecks,
  AlertCircle
} from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";

const ServicesQualidade = () => {
  const [activeTab, setActiveTab] = useState("nao-conformidades");

  const services = [
    {
      id: "nao-conformidades",
      icon: AlertTriangle,
      title: "Controle de Não Conformidades",
      description: "Sistema completo para registro, tratamento e acompanhamento de não conformidades",
      color: "from-fs-magenta-500 to-fs-purple-500",
      details: {
        overview: "Gerencie todas as não conformidades da sua empresa de forma sistemática, desde o registro inicial até a verificação da eficácia das ações corretivas, garantindo melhoria contínua e conformidade com normas ISO.",
        features: [
          "Registro detalhado de não conformidades",
          "Classificação automática por gravidade",
          "Workflow de tratamento e aprovação",
          "Planos de ação corretiva e preventiva",
          "Acompanhamento de prazos e responsáveis",
          "Relatórios gerenciais e indicadores"
        ],
        benefits: [
          "Redução de 60% nas não conformidades recorrentes",
          "Conformidade garantida com ISO 9001",
          "Rastreabilidade completa de ações",
          "Melhoria contínua dos processos"
        ],
        processes: [
          "Identificação da não conformidade",
          "Registro no sistema",
          "Análise de causa raiz",
          "Definição de ações corretivas",
          "Implementação das ações",
          "Verificação da eficácia"
        ]
      }
    },
    {
      id: "documentos",
      icon: FileText,
      title: "Gestão de Documentos",
      description: "Biblioteca digital centralizada com controle de versões e aprovações",
      color: "from-fs-purple-500 to-fs-cyan-500",
      details: {
        overview: "Centralize todos os documentos da qualidade em uma biblioteca digital inteligente, com controle rigoroso de versões, aprovações automáticas e acesso facilitado para toda a organização.",
        features: [
          "Biblioteca digital centralizada",
          "Controle automático de versões",
          "Workflow de aprovação de documentos",
          "Distribuição controlada e rastreável",
          "Busca avançada e categorização",
          "Notificações de revisão automáticas"
        ],
        benefits: [
          "Acesso instantâneo a documentos atualizados",
          "Eliminação de documentos obsoletos",
          "Controle total de distribuição",
          "Conformidade com requisitos normativos"
        ],
        processes: [
          "Criação/atualização do documento",
          "Revisão técnica",
          "Aprovação hierárquica",
          "Distribuição controlada",
          "Controle de acesso",
          "Revisão periódica"
        ]
      }
    },
    {
      id: "producao",
      icon: BarChart3,
      title: "Acompanhamento da Produção",
      description: "Monitoramento em tempo real dos processos produtivos e indicadores de qualidade",
      color: "from-fs-cyan-500 to-fs-magenta-500",
      details: {
        overview: "Monitore todos os aspectos da produção em tempo real, desde indicadores de qualidade até performance de equipamentos, garantindo que todos os processos operem dentro dos padrões estabelecidos.",
        features: [
          "Dashboards em tempo real",
          "Indicadores de qualidade automáticos",
          "Monitoramento de equipamentos",
          "Controle estatístico de processo (CEP)",
          "Alertas automáticos de desvios",
          "Relatórios de performance"
        ],
        benefits: [
          "Identificação imediata de desvios",
          "Otimização da eficiência produtiva",
          "Redução de desperdícios",
          "Garantia de qualidade do produto"
        ],
        processes: [
          "Coleta automática de dados",
          "Análise estatística",
          "Identificação de tendências",
          "Alertas preventivos",
          "Ações corretivas",
          "Análise de eficácia"
        ]
      }
    },
    {
      id: "processos-qualidade",
      icon: Settings,
      title: "Processos de Qualidade",
      description: "Automação de todos os processos do sistema de gestão da qualidade",
      color: "from-fs-purple-500 to-fs-magenta-500",
      details: {
        overview: "Automatize todos os processos do seu sistema de gestão da qualidade, desde auditorias internas até análises críticas pela direção, garantindo conformidade total com normas internacionais.",
        features: [
          "Auditorias internas digitais",
          "Análise crítica pela direção",
          "Gestão de objetivos da qualidade",
          "Controle de equipamentos de medição",
          "Gestão de fornecedores",
          "Treinamentos e competências"
        ],
        benefits: [
          "Conformidade total com ISO 9001",
          "Automação de processos críticos",
          "Evidências documentadas",
          "Melhoria contínua sistemática"
        ],
        processes: [
          "Planejamento de auditorias",
          "Execução digital",
          "Registro de evidências",
          "Relatório de auditoria",
          "Planos de ação",
          "Acompanhamento"
        ]
      }
    }
  ];

  const activeService = services.find(s => s.id === activeTab);

  const stats = [
    { icon: Target, number: "ISO 9001", label: "Conformidade Garantida" },
    { icon: TrendingUp, number: "60%", label: "Redução de NC" },
    { icon: Clock, number: "24/7", label: "Monitoramento" },
    { icon: Award, number: "100%", label: "Rastreabilidade" }
  ];

  const qualityBenefits = [
    {
      icon: Shield,
      title: "Conformidade Normativa",
      description: "Atendimento completo aos requisitos da ISO 9001 e outras normas aplicáveis"
    },
    {
      icon: TrendingUp,
      title: "Melhoria Contínua",
      description: "Ciclo sistemático de identificação, análise e melhoria dos processos"
    },
    {
      icon: Eye,
      title: "Visibilidade Total",
      description: "Dashboards e relatórios que oferecem visão completa da qualidade"
    },
    {
      icon: Zap,
      title: "Automação Inteligente",
      description: "Processos automatizados que reduzem trabalho manual e erros"
    }
  ];

  const certifications = [
    {
      name: "ISO 9001:2015",
      description: "Sistema de Gestão da Qualidade",
      icon: Award
    },
    {
      name: "ISO 14001",
      description: "Sistema de Gestão Ambiental",
      icon: Target
    },
    {
      name: "ISO 45001",
      description: "Sistema de Gestão de SST",
      icon: Shield
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Gestão da Qualidade com Fluig - ISO 9001"
        description="Sistema completo de gestão da qualidade: controle de não conformidades, gestão de documentos, auditorias digitais e compliance ISO 9001. Reduza 60% das não conformidades."
        keywords={['gestão qualidade fluig', 'iso 9001 fluig', 'não conformidades digitais', 'auditorias internas fluig', 'documentos qualidade', 'sistema gestão qualidade', 'conformidade iso', 'processos qualidade']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-fs-magenta-500/10 backdrop-blur-sm border border-fs-magenta-500/30 rounded-full px-6 py-3 mb-8">
                <Target className="w-5 h-5 text-fs-magenta-400" />
                <span className="text-foreground dark:text-white/90 font-medium">
                  Gestão da Qualidade
                </span>
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-6xl font-bold font-poppins text-foreground dark:text-white mb-6">
                Sistema de Gestão
                <span className="block fs-gradient-text mt-2">
                  da Qualidade
                </span>
              </h1>

              {/* Description */}
              <p className="text-xl text-muted-foreground dark:text-white/70 leading-relaxed mb-12 max-w-3xl mx-auto">
                Implemente um sistema robusto de gestão da qualidade com controle total
                de não conformidades, documentos e processos produtivos.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                {stats.map((stat, index) => (
                  <div key={stat.label} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-fs-magenta-500 to-fs-purple-500 mb-3">
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-2xl md:text-3xl font-bold fs-gradient-text mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-muted-foreground dark:text-white/70">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Button variant="fs-primary" size="lg" className="group">
                  Solicitar Auditoria
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="fs-outline" size="lg">
                  Ver Certificações
                </Button>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/30">
          <Container>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Por que investir em
                <span className="block fs-gradient-text mt-2">
                  Gestão da Qualidade?
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Um sistema robusto de qualidade é essencial para competitividade e sustentabilidade.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {qualityBenefits.map((benefit, index) => (
                <Card key={benefit.title} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:-translate-y-2 transition-all duration-500">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-fs-magenta-500 to-fs-purple-500 mb-4">
                      <benefit.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Certifications Section */}
        <section className="py-20 bg-fs-section">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Normas e
                <span className="block fs-gradient-text mt-2">
                  Certificações
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-muted-foreground dark:text-white/80 max-w-2xl mx-auto mb-8">
                Nosso sistema atende aos requisitos das principais normas internacionais.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {certifications.map((cert, index) => (
                <Card key={cert.name} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:-translate-y-2 transition-all duration-500">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-fs-magenta-500 to-fs-purple-500 mb-4">
                      <cert.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">
                      {cert.name}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 text-sm">
                      {cert.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Services Navigation */}
        <section className="py-12 bg-fs-section border-b border-border">
          <Container>
            <div className="flex flex-wrap justify-center gap-4">
              {services.map((service) => {
                const IconComponent = service.icon;
                return (
                  <button
                    key={service.id}
                    onClick={() => setActiveTab(service.id)}
                    className={`flex items-center space-x-3 px-6 py-3 rounded-xl transition-all duration-300 ${
                      activeTab === service.id
                        ? 'bg-gradient-to-r from-fs-magenta-500 to-fs-purple-500 text-white scale-105'
                        : 'bg-card dark:bg-gray-900/50 text-foreground dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-gray-900/70'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="font-medium">{service.title}</span>
                  </button>
                );
              })}
            </div>
          </Container>
        </section>

        {/* Active Service Details */}
        {activeService && (
          <section className="py-20 bg-fs-section">
            <Container>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Service Info */}
                <div>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${activeService.color} p-4`}>
                      <activeService.icon className="w-full h-full text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold font-poppins text-foreground dark:text-white">
                        {activeService.title}
                      </h2>
                      <p className="text-fs-magenta-400">{activeService.description}</p>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {/* Overview */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Como Funciona</h3>
                      <p className="text-muted-foreground dark:text-white/70 leading-relaxed">
                        {activeService.details.overview}
                      </p>
                    </div>

                    {/* Features */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Principais Funcionalidades</h3>
                      <ul className="space-y-3">
                        {activeService.details.features.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <CheckCircle2 className="w-5 h-5 text-fs-magenta-400 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground dark:text-white/70">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Process Flow */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Fluxo do Processo</h3>
                      <div className="space-y-3">
                        {activeService.details.processes.map((process, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-fs-magenta-500 to-fs-purple-500 flex items-center justify-center text-white text-xs font-bold">
                              {index + 1}
                            </div>
                            <span className="text-muted-foreground dark:text-white/70">{process}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Benefits and ROI */}
                <div className="space-y-8">
                  {/* Benefits Card */}
                  <Card className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-3 text-foreground dark:text-white">
                        <Zap className="w-6 h-6 text-fs-magenta-400" />
                        <span>Principais Benefícios</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {activeService.details.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-fs-purple-400 rounded-full flex-shrink-0 mt-2" />
                            <span className="text-muted-foreground dark:text-white/70">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Quality Metrics Card */}
                  <Card className="bg-gradient-to-br from-fs-magenta-500/20 to-fs-purple-500/20 border border-fs-magenta-500/30">
                    <CardContent className="p-6">
                      <div className="text-center mb-4">
                        <BarChart3 className="w-12 h-12 text-fs-magenta-400 mx-auto mb-3" />
                        <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">
                          Indicadores de Qualidade
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold fs-gradient-text">99.7%</div>
                          <div className="text-xs text-muted-foreground dark:text-white/70">Conformidade</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold fs-gradient-text">60%</div>
                          <div className="text-xs text-muted-foreground dark:text-white/70">Redução de NC</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* CTA Card */}
                  <Card className="bg-gradient-to-br from-fs-purple-500/20 to-fs-cyan-500/20 border border-fs-purple-500/30">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-xl font-bold text-foreground dark:text-white mb-3">
                        Pronto para certificar?
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 mb-6">
                        Nossos especialistas podem ajudar você a implementar 
                        um sistema de qualidade robusto e eficiente.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button variant="fs-primary" className="flex-1">
                          <Award className="w-4 h-4 mr-2" />
                          Solicitar Auditoria
                        </Button>
                        <Button variant="fs-outline" className="flex-1">
                          Ver Metodologia
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </Container>
          </section>
        )}

        {/* Other Services Section */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Outros Serviços
                <span className="block fs-gradient-text mt-2">
                  Especializados
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Explore nossas outras áreas de expertise para uma solução completa.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Tecnologia da Informação",
                  description: "Outsourcing Fluig, desenvolvimento JavaScript/React e soluções Python.",
                  icon: MonitorPlay,
                  href: "/servicos/ti",
                  color: "from-fs-purple-500 to-fs-cyan-500"
                },
                {
                  title: "Recursos Humanos",
                  description: "Automação de processos de RH com admissão digital e gestão de férias.",
                  icon: Users,
                  href: "/servicos/rh",
                  color: "from-fs-cyan-500 to-fs-magenta-500"
                },
                {
                  title: "Processos Financeiros",
                  description: "Automação de compras, fornecedores e reembolsos de despesas.",
                  icon: TrendingUp,
                  href: "/servicos/financeiro",
                  color: "from-fs-magenta-500 to-fs-purple-500"
                }
              ].map((service, index) => (
                <Link key={service.title} to={service.href}>
                  <Card className="group bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20 hover:-translate-y-2 transition-all duration-500 hover:shadow-fs-hover hover:border-fs-purple-500/50 h-full">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} p-3 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <service.icon className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground dark:text-white mb-3 group-hover:fs-gradient-text transition-all duration-300">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <Button variant="fs-outline" size="sm" className="w-full group-hover:bg-fs-primary/20">
                        Saiba Mais
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </Container>
        </section>

        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Soluções Complementares"
              links={[
                {
                  title: "Processos Financeiros",
                  description: "Automatize solicitações de compras, cadastro de fornecedores e reembolsos com workflows integrados.",
                  href: "/servicos/financeiro",
                  category: "Serviços"
                },
                {
                  title: "Serviços de Recursos Humanos",
                  description: "Digitalize admissão, gestão de férias e desligamentos com processos 100% automatizados.",
                  href: "/servicos/rh",
                  category: "Serviços"
                },
                {
                  title: "Consultoria em IA",
                  description: "Implemente inteligência artificial nos seus processos de qualidade com nossa metodologia especializada.",
                  href: "/consultoria-ia",
                  category: "Consultoria"
                },
                {
                  title: "Catálogo FluigApps",
                  description: "Acelere implementação com nossos 16 FluigApps prontos para gestão da qualidade e outros processos.",
                  href: "/flows",
                  category: "Soluções"
                }
              ]}
            />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <Container>
            <FAQSection 
              title="Perguntas Frequentes - Gestão da Qualidade"
              description="Esclareça suas dúvidas sobre nossos serviços de Gestão da Qualidade"
              faqs={[
                {
                  question: "Como funciona o controle de não conformidades no sistema?",
                  answer: "Nosso sistema permite registrar não conformidades de forma detalhada, classificando automaticamente por gravidade e tipo. Cada NC possui workflow específico para análise de causa raiz, definição de ações corretivas e preventivas, e acompanhamento até a verificação da eficácia. O sistema gera alertas automáticos para prazos e permite anexar evidências fotográficas. Todas as ações são rastreáveis e geram relatórios para análise crítica pela direção."
                },
                {
                  question: "O sistema de gestão de documentos garante controle de versões?",
                  answer: "Sim, nosso sistema de gestão documental possui controle rigoroso de versões com histórico completo de alterações. Quando um documento é atualizado, a versão anterior é automaticamente arquivada e todas as cópias distribuídas são invalidadas. O sistema notifica automaticamente os usuários sobre novas versões e possui workflow de aprovação hierárquica. Também oferece controle de acesso por perfil e rastreabilidade completa de quem acessou cada documento."
                },
                {
                  question: "Como são realizadas as auditorias internas digitais?",
                  answer: "As auditorias internas são totalmente digitalizadas através de checklists inteligentes adaptáveis a cada processo. Os auditores utilizam tablets ou smartphones para registrar evidências, não conformidades e observações em tempo real. O sistema permite anexar fotos, documentos e gravações de áudio. Ao final, gera automaticamente o relatório de auditoria com planos de ação associados. Todo o processo é paperless e as evidências ficam armazenadas de forma segura e acessível."
                },
                {
                  question: "O sistema atende aos requisitos da ISO 9001:2015?",
                  answer: "Nosso sistema foi desenvolvido especificamente para atender integralmente aos requisitos da ISO 9001:2015 e outras normas relacionadas. Inclui todos os controles obrigatórios como gestão de riscos, análise crítica pela direção, controle de documentos e registros, auditorias internas, ações corretivas e melhoria contínua. Fornecemos mapeamento completo entre funcionalidades do sistema e requisitos normativos, facilitando certificações e auditorias externas."
                },
                {
                  question: "Como funciona o módulo de treinamentos e capacitação?",
                  answer: "O módulo de treinamentos permite cadastrar competências necessárias por função, criar matriz de habilidades e programar treinamentos automáticos. O sistema identifica gaps de competência e sugere treinamentos personalizados. Colaboradores podem acessar conteúdo online, fazer avaliações e receber certificados digitais. Gestores acompanham o progresso da equipe através de dashboards e o sistema mantém histórico completo para auditorias e avaliações de desempenho."
                },
                {
                  question: "Que tipos de relatórios de qualidade estão disponíveis?",
                  answer: "O sistema oferece mais de 50 relatórios pré-configurados incluindo: indicadores de qualidade em tempo real, análise de tendências de não conformidades, performance de fornecedores, eficácia de ações corretivas, status de auditorias e cronogramas de treinamento. Todos os relatórios são interativos com filtros avançados e podem ser exportados em diversos formatos. Também permite criar dashboards personalizados com KPIs específicos do negócio e agendar envio automático por email."
                }
              ]}
            />
          </Container>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default ServicesQualidade;